import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPencil, HiTrash, HiX, HiCube } from 'react-icons/hi';
import { Modal, Button } from '@/components/common';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { serviceApi } from '@/api/endpoints/services';
import { Service, ServiceLine, SERVICE_STATUS_SELLING, SERVICE_STATUS_PENDING, SERVICE_STATUS_STOP } from '@/types/service';
import { formatDate, formatCurrency } from '@/utils/safe-formatting';

interface ServiceDetailModalProps {
  onDeleteSuccess?: () => void;
}

export const ServiceDetailModal: React.FC<ServiceDetailModalProps> = ({
  onDeleteSuccess,
}) => {
  const { t } = useTranslation();
  const { modals, closeModal, openModal, modalData } = useUIStore();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isOpen = modals.serviceDetail || false;
  const service = modalData?.service as Service | undefined;
  const serviceLines: ServiceLine[] = service?.serviceLines || [];

  const handleEdit = () => {
    if (service) {
      closeModal('serviceDetail');
      openModal('editService', { service });
    }
  };

  const handleDelete = async () => {
    if (!service) return;
    
    if (!confirm(t('services.messages.deleteConfirm'))) {
      return;
    }
    
    setDeleteLoading(true);
    try {
      await serviceApi.deleteService(service.code);
      toast.success('Success', t('services.messages.deleteSuccess'));
      closeModal('serviceDetail');
      onDeleteSuccess?.();
    } catch (error: any) {
      console.error('Error deleting service:', error);
      const errorMessage = error.message || t('services.messages.deleteError');
      toast.error('Error', errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('serviceDetail');
  };

  // Remove local formatPrice and formatDate functions since we're using the safe ones from utils

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      [SERVICE_STATUS_SELLING]: {
        label: t('services.status.selling'),
        className: 'bg-green-100 text-green-800',
      },
      [SERVICE_STATUS_PENDING]: {
        label: t('services.status.pending'),
        className: 'bg-yellow-100 text-yellow-800',
      },
      [SERVICE_STATUS_STOP]: {
        label: t('services.status.stop'),
        className: 'bg-red-100 text-red-800',
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig[SERVICE_STATUS_PENDING];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (!service) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('services.modals.detailTitle')}
      size="xl"
    >
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{service.name}</h3>
            <p className="text-sm text-gray-500">Mã: {service.code}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              icon={<HiPencil className="h-4 w-4" />}
              onClick={handleEdit}
            >
              {t('services.labels.editService')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<HiTrash className="h-4 w-4" />}
              onClick={handleDelete}
              loading={deleteLoading}
              className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
            >
              {t('services.labels.deleteService')}
            </Button>
          </div>
        </div>

        {/* Service details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Danh mục</label>
            <p className="mt-1 text-sm text-gray-900">{service.categoryName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
            <div className="mt-1">
              {getStatusBadge(service.status)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Giá cơ bản</label>
            <p className="mt-1 text-sm text-gray-900 font-medium">
              {formatCurrency(service.price)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phòng</label>
            <p className="mt-1 text-sm text-gray-900">{service.room}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Ngày tạo</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(service.createdAt)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Ngày cập nhật</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(service.updatedAt)}</p>
          </div>
        </div>

        {/* Service Lines (NVL Configuration) Table */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-medium text-gray-900 flex items-center">
              <HiCube className="h-5 w-5 mr-2 text-blue-600" />
              Danh sách cấu hình NVL
              {serviceLines.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {serviceLines.length}
                </span>
              )}
            </h4>
          </div>

          {serviceLines.length > 0 ? (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      STT
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mã NVL
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tên NVL
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Số lượng
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tỷ lệ hao hụt (%)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serviceLines.map((line, index) => (
                    <tr key={line.id || line.code || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {line.materialId}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {line.materialName || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {line.quantity?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {line.loseRate?.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
              <HiCube className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">Chưa có cấu hình nguyên vật liệu</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            icon={<HiX className="h-4 w-4" />}
          >
            Đóng
          </Button>
        </div>
      </div>
    </Modal>
  );
};
